/**
 * Post-link rules for campaign analytics — the same checks the web app applies
 * before POST /analytics/post/upload (clanconnect_2.0 formValidation.ts:
 * normalizeInstagramPostUrl / CheckInstagramUrl / CheckYouTubeUrl, and the
 * duplicate check in add_analytics_brand.tsx / excel-post-links-upload.tsx), so a
 * link accepted by the app is accepted by the web and vice versa.
 */

export type PostPlatform = 'Instagram' | 'Youtube';

const INSTAGRAM_POST = /^(https?:\/\/)?(www\.)?instagram\.com\/(p|reel|tv)\/([^/?#&]+)/;
const YOUTUBE_POST = /^(https?:\/\/)?(www\.)?(youtube\.com\/(watch\?v=|embed\/|shorts\/)|youtu\.be\/)\S+$/;

/** instagram.com/<username>/reel/<code> -> https://www.instagram.com/reel/<code>/ (what Instagram's share copies). */
export function normalizePostUrl(url: string): string {
  const value = (url || '').trim();
  const match = value.match(/^(?:https?:\/\/)?(?:www\.|m\.)?instagram\.com\/(?!p\/|reel\/|reels\/|tv\/)[A-Za-z0-9_.]+\/(p|reel|reels|tv)\/([A-Za-z0-9_-]+)/i);
  if (!match) return value;
  const type = match[1].toLowerCase() === 'reels' ? 'reel' : match[1].toLowerCase();
  return `https://www.instagram.com/${type}/${match[2]}/`;
}

export function platformOfPostUrl(url: string): PostPlatform | null {
  const value = (url || '').toLowerCase();
  if (value.includes('instagram.com')) return 'Instagram';
  if (value.includes('youtube.com') || value.includes('youtu.be')) return 'Youtube';
  return null;
}

/**
 * null when the (already normalised) link is a valid post of one of `allowedPlatforms`
 * ('Instagram' / 'YouTube' / 'Youtube', any case; empty = Instagram or YouTube),
 * otherwise a user-facing reason.
 */
export function postUrlProblem(url: string, allowedPlatforms: string[]): string | null {
  const value = (url || '').trim();
  if (!value) return 'This field is required!';
  const platform = platformOfPostUrl(value);
  // Only Instagram/YouTube posts get analytics; a brief on other platforms only
  // (or none) accepts either.
  const tracked = allowedPlatforms.map((p) => p.trim().toLowerCase()).filter((p) => p === 'instagram' || p === 'youtube');
  const allowed = tracked.length ? tracked : ['instagram', 'youtube'];
  if (!platform || !allowed.includes(platform.toLowerCase())) {
    const names = allowed.map((p) => (p === 'instagram' ? 'Instagram' : 'YouTube')).join(' / ');
    return `Only ${names} post links can be added here.`;
  }
  if (platform === 'Instagram' && !INSTAGRAM_POST.test(value)) return 'Please enter valid instagram url';
  if (platform === 'Youtube' && !YOUTUBE_POST.test(value)) return 'Please enter valid youtube url';
  return null;
}

/** The web's duplicate rule: one link contains the other (blank entries never match). */
export function isDuplicatePostUrl(url: string, others: string[]): boolean {
  const value = (url || '').trim();
  if (!value) return false;
  return others.some((other) => {
    const existing = (other || '').trim();
    return !!existing && (existing.includes(value) || value.includes(existing));
  });
}

/**
 * Builds the list to submit from the typed URL fields plus the links imported from
 * Excel (already checked on import). Typed links are normalised and checked like
 * the web's Add Post form; the first problem is returned as `error`.
 */
export function preparePostLinks(opts: {
  typed: string[];
  imported: string[];
  platforms: string[];
  alreadyAdded: string[];
}): { links: string[]; error?: string } {
  const typed = opts.typed.map((u) => u.trim()).filter(Boolean);
  const links: string[] = [];
  for (let i = 0; i < typed.length; i++) {
    const label = typed.length > 1 ? `Link ${i + 1}: ` : '';
    const link = normalizePostUrl(typed[i]);
    const problem = postUrlProblem(link, opts.platforms);
    if (problem) return { links: [], error: `${label}${problem}` };
    if (isDuplicatePostUrl(link, links)) return { links: [], error: `${label}This link is repeated.` };
    if (isDuplicatePostUrl(link, opts.alreadyAdded)) return { links: [], error: `${label}This link is already added to this campaign.` };
    if (isDuplicatePostUrl(link, opts.imported)) return { links: [], error: `${label}This link is already in the Excel list.` };
    links.push(link);
  }
  // Excel links were checked on import, but the allowed platform may have changed since.
  const offPlatform = opts.imported.filter((link) => postUrlProblem(link, opts.platforms)).length;
  if (offPlatform) {
    return { links: [], error: `${offPlatform} link${offPlatform === 1 ? '' : 's'} from Excel ${offPlatform === 1 ? 'is' : 'are'} not for this platform. Clear the Excel list and upload again.` };
  }
  links.push(...opts.imported);
  if (!links.length) return { links: [], error: 'Add at least one post link.' };
  return { links };
}

/**
 * Optional creator username on an Instagram post link (campaign_analytics.username).
 * If Instagram won't show us a post, the analytics worker looks for it on this
 * creator's profile instead. Same rule as the API (analytics/ig-username.ts) and
 * the worker: letters, numbers, '.' and '_' only, up to 30 characters.
 */
const IG_USERNAME = /^[A-Za-z0-9._]{1,30}$/;
export const IG_USERNAME_ERROR = "Enter a valid Instagram username: letters, numbers, '.' and '_' only, up to 30 characters.";

/** "name", "@name" or a pasted profile link -> the bare handle. Blank -> ''. Anything else -> error. */
export function parseIgUsername(raw: string | undefined | null): { username: string; error?: string } {
  let value = String(raw ?? '').trim();
  if (!value) return { username: '' };
  const fromLink = value.match(/instagram\.com\/([^/?#\s]+)/i);
  if (fromLink) {
    if (['p', 'reel', 'reels', 'tv', 'stories'].includes(fromLink[1].toLowerCase())) {
      return { username: '', error: "That is a post link. Enter the creator's username instead." };
    }
    value = fromLink[1];
  }
  value = value.replace(/^@+/, '');
  return IG_USERNAME.test(value) ? { username: value } : { username: '', error: IG_USERNAME_ERROR };
}

/** Whether links for these platforms can be Instagram (empty = Instagram or YouTube), i.e. the username box is offered. */
export function allowsInstagram(platforms: string[]): boolean {
  const tracked = platforms.map((p) => p.trim().toLowerCase()).filter((p) => p === 'instagram' || p === 'youtube');
  return tracked.length === 0 || tracked.includes('instagram');
}

/** The username box is shown on a row unless its link is clearly not an Instagram link. */
export function rowTakesUsername(url: string): boolean {
  const platform = platformOfPostUrl(url);
  return !url.trim() || platform === 'Instagram';
}

/**
 * Pairs `links` (from preparePostLinks: the typed rows first, in order, then the Excel
 * links) with their optional usernames. Only Instagram links keep one. The first
 * invalid username is returned as `error`, labelled like preparePostLinks' errors.
 */
export function withPostUsernames(opts: {
  links: string[];
  typed: string[];
  typedUsernames: string[];
  importedUsernames: Record<string, string>;
}): { postLinks: { url: string; username?: string }[]; error?: string } {
  const typedRows = opts.typed.map((url, i) => ({ url: url.trim(), username: opts.typedUsernames[i] ?? '' })).filter((r) => r.url);
  const postLinks: { url: string; username?: string }[] = [];
  for (let i = 0; i < opts.links.length; i++) {
    const url = opts.links[i];
    const typed = i < typedRows.length;
    const raw = typed ? typedRows[i].username : opts.importedUsernames[url] ?? '';
    if (platformOfPostUrl(url) !== 'Instagram' || !raw.trim()) {
      postLinks.push({ url });
      continue;
    }
    const { username, error } = parseIgUsername(raw);
    if (error) {
      const label = typed ? (typedRows.length > 1 ? `Link ${i + 1} username: ` : 'Username: ') : `Username for ${url}: `;
      return { postLinks: [], error: `${label}${error}` };
    }
    postLinks.push({ url, username });
  }
  return { postLinks };
}
